import dayjs, { Dayjs } from "utils/dayjs"

import { Guid, IApiLink } from "@/types"
import AmountWithCurrency, { IAmountWithCurrencyDto } from "./AmountWithCurrency"
import { ApiLinkEntity, ApiLinkRel } from "config/constants"
import { toDateWithoutTimezone } from "utils/utils"

export enum InvoiceAssignmentStatus {
    assigned = "assigned",
    notAssigned = "notAssigned",
    assignedAutomatically = "assignedAutomatically",
    notRelevant = "notRelevant"
}

export enum InvoiceAutomaticAssignStatus {
    success = "success",
    limitLessThanAmount = "limitLessThanAmount",
    noRelevantAssets = "noRelevantAssets",
    noReferenceValues = "noReferenceValues"
}

export interface IGetInvoiceDto {
    id: Guid
    invoiceDate: string
    invoiceNumber: string
    invoiceAmount: IAmountWithCurrencyDto
    invoiceForeignCurrencyAmount?: IAmountWithCurrencyDto | null
    netInvoiceAmount: IAmountWithCurrencyDto
    netInvoiceForeignCurrencyAmount?: IAmountWithCurrencyDto | null
    discountPercentage: number
    requestNumber?: string
    requestId?: string
    isCustomerRequestDeleted: boolean
    referenceNumber: string
    referenceText?: string
    assignmentStatus: InvoiceAssignmentStatus
    costCategoryName: string
    costCategoryId: Guid
    costCategoryLedgerAccount?: string | null
    bookingPeriod: string
    supplierId?: Guid | null
    createdByAssetSplit: boolean
    setNotRelevantByUserId?: Guid
    setNotRelevantDate?: string
    links?: IApiLink[]
}

export interface IPostInvoiceDto {
    costCategoryId: Guid
    invoiceDate: string
    invoiceNumber: string
    invoiceAmount: IAmountWithCurrencyDto
    invoiceForeignCurrencyAmount?: IAmountWithCurrencyDto
    discountPercentage: number
    requestNumber?: string
    referenceNumber: string
    referenceText?: string
    bookingPeriod: string
    supplierId?: Guid | null
    createdByAssetSplit?: boolean
    invoiceImportItemId?: Guid
}

export interface IPostInvoiceAssetAssignmentDto {
    assetId: Guid
    assignedAmount: IAmountWithCurrencyDto
}

export default class Invoice {
    id: Guid
    invoiceDate: Dayjs
    invoiceNumber: string
    invoiceAmount: AmountWithCurrency
    invoiceForeignCurrencyAmount?: AmountWithCurrency
    netInvoiceAmount: AmountWithCurrency
    netInvoiceForeignCurrencyAmount?: AmountWithCurrency
    discountPercentage: number
    requestNumber?: string
    requestId?: string
    isCustomerRequestDeleted: boolean
    referenceNumber: string
    referenceText?: string
    assignmentStatus: InvoiceAssignmentStatus
    costCategoryName: string
    costCategoryId: Guid
    costCategoryLedgerAccount?: string | null
    bookingPeriod: Dayjs
    supplierId?: Guid | null
    createdByAssetSplit?: boolean
    setNotRelevantByUserId?: Guid
    setNotRelevantDate?: Dayjs
    getAssetBookingViewsLink?: IApiLink
    createInvoiceAssetAssignmentsLink?: IApiLink
    getDetailsLink?: IApiLink
    updateLink?: IApiLink
    setNotRelevantLink?: IApiLink

    constructor({
        id,
        invoiceDate,
        invoiceNumber,
        invoiceAmount,
        invoiceForeignCurrencyAmount,
        netInvoiceAmount,
        netInvoiceForeignCurrencyAmount,
        discountPercentage,
        requestNumber,
        isCustomerRequestDeleted,
        requestId,
        referenceNumber,
        referenceText,
        costCategoryName,
        costCategoryId,
        costCategoryLedgerAccount,
        assignmentStatus,
        bookingPeriod,
        supplierId,
        createdByAssetSplit,
        setNotRelevantByUserId,
        setNotRelevantDate,
        links
    }: IGetInvoiceDto) {
        this.id = id
        this.invoiceDate = dayjs(invoiceDate, "YYYY-MM-DD") // take only date ignoring the time
        this.invoiceNumber = invoiceNumber
        this.invoiceAmount = new AmountWithCurrency(invoiceAmount)
        this.invoiceForeignCurrencyAmount = invoiceForeignCurrencyAmount ? new AmountWithCurrency(invoiceForeignCurrencyAmount) : undefined
        this.netInvoiceAmount = new AmountWithCurrency(netInvoiceAmount)
        this.netInvoiceForeignCurrencyAmount = netInvoiceForeignCurrencyAmount ? new AmountWithCurrency(netInvoiceForeignCurrencyAmount) : undefined
        this.discountPercentage = discountPercentage
        this.requestNumber = requestNumber
        this.requestId = requestId
        this.isCustomerRequestDeleted = isCustomerRequestDeleted
        this.referenceNumber = referenceNumber
        this.referenceText = referenceText
        this.assignmentStatus = assignmentStatus
        this.costCategoryName = costCategoryName
        this.costCategoryId = costCategoryId
        this.costCategoryLedgerAccount = costCategoryLedgerAccount
        this.bookingPeriod = dayjs(bookingPeriod, "YYYY-MM")
        this.supplierId = supplierId ?? null
        this.createdByAssetSplit = createdByAssetSplit
        this.setNotRelevantByUserId = setNotRelevantByUserId
        this.setNotRelevantDate = setNotRelevantDate ? dayjs(setNotRelevantDate) : undefined
        this.getAssetBookingViewsLink = links?.find(lnk => lnk.entity === ApiLinkEntity.assetBookingView && lnk.rel === ApiLinkRel.all)
        this.createInvoiceAssetAssignmentsLink = links?.find(lnk => lnk.entity === ApiLinkEntity.invoiceAssetAssignment && lnk.rel === ApiLinkRel.new)
        this.getDetailsLink = links?.find(lnk => lnk.entity === ApiLinkEntity.invoiceDetails && lnk.rel === ApiLinkRel.self)
        this.updateLink = links?.find(lnk => lnk.entity === ApiLinkEntity.invoice && lnk.rel === ApiLinkRel.partialUpdate)
        this.setNotRelevantLink = links?.find(lnk => lnk.entity === ApiLinkEntity.invoice && lnk.rel === ApiLinkRel.update)
    }

    toPostDto(): IPostInvoiceDto {
        return {
            costCategoryId: this.costCategoryId,
            invoiceDate: toDateWithoutTimezone(this.invoiceDate),
            invoiceNumber: this.invoiceNumber,
            invoiceAmount: this.invoiceAmount,
            invoiceForeignCurrencyAmount: this.invoiceForeignCurrencyAmount,
            discountPercentage: this.discountPercentage,
            requestNumber: this.requestNumber,
            referenceNumber: this.referenceNumber,
            referenceText: this.referenceText,
            bookingPeriod: toDateWithoutTimezone(this.bookingPeriod),
            supplierId: this.supplierId
        }
    }
}
